// "Nice, here are 3 more jobs that fit you" — the success step of the apply dialog.
//
// Shown INSIDE the apply dialog right after an application is sent (JobDetails.js). Two thirds
// of seekers only ever apply once, so the moment after the first Submit is where application
// number two and three are won. Each row is a job the dashboard's matcher already ranked for
// this seeker (backend /api/applications/next-jobs). Tapping Apply opens a small in-dialog
// apply step with the cover letter PRE-FILLED from the application just sent — editable, so a
// letter written for the first job (its title, its keywords) can be adjusted before it goes to
// a different employer. Nothing is sent until the seeker presses Submit on that step.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle, MapPin, Loader2, ArrowLeft, Briefcase } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

const jobUrl = (job) => (job.slug ? `/jobs/${job.slug}` : `/jobs/${job.id}`);

const formatPay = (job) => {
  const min = job.salary_min ? `₱${Number(job.salary_min).toLocaleString()}` : '';
  const max = job.salary_max ? `₱${Number(job.salary_max).toLocaleString()}` : '';
  const range = min && max ? `${min}-${max}` : min || max;
  if (!range) return '';
  return job.salary_period ? `${range}/${job.salary_period}` : range;
};

export default function NextJobsPanel({
  t,
  jobs = [],
  loading = false,
  defaultCover = '',
  appliedIds = new Set(),
  onApply,          // async (job, { cover, customAnswer }) => boolean
  onDone,           // () => void  (closes the dialog)
}) {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);   // the job whose apply step is open
  const [cover, setCover] = useState('');
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const openApplyStep = (job) => {
    setActive(job);
    setCover(defaultCover || '');   // fresh copy per job — edits never leak to the next one
    setAnswer('');
  };

  const submit = async () => {
    if (!active || submitting) return;
    if (active.custom_question && !answer.trim()) {
      toast.error(t('jobDetails.answerRequired', "Please answer the employer's screening question"));
      return;
    }
    setSubmitting(true);
    try {
      const ok = await onApply(active, {
        cover,
        customAnswer: active.custom_question ? answer : null,
      });
      if (ok) setActive(null);
    } finally {
      setSubmitting(false);
    }
  };

  const viewJob = (job) => {
    onDone();
    navigate(jobUrl(job));
  };

  // ── Step 2: apply to one suggested job (pre-filled, editable cover letter) ──────────
  if (active) {
    return (
      <>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Briefcase size={18} className="text-primary" />
            {t('jobDetails.nextJobsApplyingTo', 'Applying to')} {active.title}
          </DialogTitle>
          <DialogDescription className="text-left">
            {[active.employer_name, active.location].filter(Boolean).join(' · ')}
            {formatPay(active) ? ` · ${formatPay(active)}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="py-3 space-y-4 overflow-y-auto flex-1" data-testid="next-job-apply-step">
          <div>
            <Label htmlFor="next-job-cover">{t('jobDetails.coverLetter', 'Cover Letter')}</Label>
            <p className="text-xs text-muted-foreground mb-1">
              {t('jobDetails.nextJobsCoverHint', 'Pre-filled from the application you just sent. Edit anything that was specific to the other job.')}
            </p>
            <Textarea
              id="next-job-cover"
              data-testid="next-job-cover"
              value={cover}
              onChange={(e) => setCover(e.target.value)}
              rows={6}
              maxLength={5000}
            />
          </div>
          {active.custom_question && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
              <p className="text-sm font-medium text-blue-900">
                {t('jobDetails.employerQuestion', "Employer's question")}
              </p>
              <p className="text-sm text-blue-800">{active.custom_question}</p>
              <Textarea
                data-testid="next-job-answer"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows={3}
                maxLength={1000}
              />
            </div>
          )}
        </div>
        <DialogFooter className="flex-shrink-0 gap-2">
          <Button variant="outline" onClick={() => setActive(null)} disabled={submitting}>
            <ArrowLeft size={14} className="mr-1" />
            {t('jobDetails.nextJobsBack', 'Back to list')}
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="next-job-submit">
            {submitting
              ? t('jobDetails.submitting', 'Submitting...')
              : t('jobDetails.submitApplication', 'Submit Application')}
          </Button>
        </DialogFooter>
      </>
    );
  }

  // ── Step 1: success + the list ──────────────────────────────────────────────────────
  return (
    <>
      <DialogHeader className="flex-shrink-0">
        <DialogTitle className="flex items-center gap-2 text-green-700">
          <CheckCircle size={20} />
          {t('jobDetails.nextJobsSent', 'Application sent!')}
        </DialogTitle>
        <DialogDescription className="text-left">
          {loading
            ? t('jobDetails.nextJobsLoading', 'Finding more jobs that fit you...')
            : jobs.length > 0
              ? t('jobDetails.nextJobsTitle', 'Nice! Here are {{count}} more jobs that fit you', { count: jobs.length })
              : t('jobDetails.nextJobsEmpty', 'No other matching jobs right now.')}
        </DialogDescription>
      </DialogHeader>

      <div className="py-2 space-y-2 overflow-y-auto flex-1" data-testid="next-jobs-list">
        {loading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="animate-spin mr-2" size={18} />
          </div>
        )}

        {!loading && jobs.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('jobDetails.nextJobsSubtitle', 'Your cover letter is pre-filled — review or edit it for each job before you send.')}
          </p>
        )}

        {!loading && jobs.map((job) => {
          const applied = appliedIds.has(job.id);
          return (
            <div
              key={job.id}
              data-testid="next-job-row"
              className="border rounded-xl p-3 flex items-start justify-between gap-3"
            >
              <div className="min-w-0">
                <button
                  type="button"
                  className="font-semibold text-sm text-left hover:underline line-clamp-2"
                  onClick={() => viewJob(job)}
                >
                  {job.title}
                </button>
                <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2">
                  {job.employer_name && <span className="truncate">{job.employer_name}</span>}
                  {job.location && (
                    <span className="inline-flex items-center gap-0.5">
                      <MapPin size={11} />{job.location}
                    </span>
                  )}
                  {formatPay(job) && <span>{formatPay(job)}</span>}
                </div>
                {typeof job.match_score === 'number' && job.match_score > 0 && (
                  <Badge variant="secondary" className="mt-1 text-[10px]">
                    {t('jobDetails.nextJobsMatch', '{{score}}% match', { score: job.match_score })}
                  </Badge>
                )}
              </div>
              <div className="flex-shrink-0">
                {applied ? (
                  <Badge className="bg-green-600">
                    <CheckCircle size={12} className="mr-1" />
                    {t('jobDetails.nextJobsApplied', 'Applied')}
                  </Badge>
                ) : job.requires_documents ? (
                  <Button size="sm" variant="outline" onClick={() => viewJob(job)}>
                    {t('jobDetails.nextJobsView', 'View & apply')}
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => openApplyStep(job)} data-testid="next-job-apply">
                    {t('jobDetails.nextJobsApply', 'Apply')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {!loading && jobs.length === 0 && (
          <div className="text-center py-4">
            <Button variant="outline" onClick={() => { onDone(); navigate('/jobs'); }}>
              {t('jobDetails.nextJobsBrowse', 'Browse all jobs')}
            </Button>
          </div>
        )}
      </div>

      <DialogFooter className="flex-shrink-0">
        <Button onClick={onDone} data-testid="next-jobs-done">
          {t('jobDetails.nextJobsDone', 'Done')}
        </Button>
      </DialogFooter>
    </>
  );
}
